import { useTranslations } from '@vocab/react';
import { Inline, Stack, Text } from 'braid-design-system';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, type Dispatch } from 'redux';

import { MAX_AD_AGE_IN_DAYS } from 'src/app/constants/adExpiry';
import { FORM_DATA_AD_EXPIRY_DATE } from 'src/app/constants/dispatcherId';
import { formData } from 'src/app/state/dispatcher';
import {
  addDays,
  getDateDiffInDays,
  isoDateFromDate,
  setUtcDateTime,
  setZonedDateTime,
} from 'src/app/utils/date';
import VocabHtmlTags from 'src/app/utils/translation/vocabHtmlTags';
import type AppState from 'src/types/appState';

import translations from './.vocab';
import { AdExpiryDateInput } from './AdExpiryDateInput';

interface Props {
  dispatchFormData: (fieldName: string, value: string) => void;
  expiryDate?: string | null;
  listingDate?: string | null;
}

const AdExpiryDate = (props: Props) => {
  const { t } = useTranslations(translations);
  const { listingDate, expiryDate, dispatchFormData } = props;
  // Date initialisers
  let listingDateInAEST;
  const expiryDateStr = expiryDate
    ? isoDateFromDate(new Date(expiryDate))
    : null;
  const nowDate = new Date();
  const nowDateStr = isoDateFromDate(nowDate);
  if (listingDate) {
    listingDateInAEST = setZonedDateTime(listingDate);
  }
  const maxDate = addDays(listingDateInAEST ?? nowDate, MAX_AD_AGE_IN_DAYS);
  const maxDateinUTC = setUtcDateTime(maxDate);
  const maxDateStr = isoDateFromDate(new Date(maxDateinUTC));
  const endDateStr = expiryDateStr ?? maxDateStr;
  const endDate = new Date(endDateStr);

  const renderAdDuration = () => {
    // calc diff between expiry - now to output number of day(s)
    const duration = getDateDiffInDays(nowDate, endDate);
    return `${duration} ${
      duration > 1
        ? t('ad-expiry-days-left', {
            statNumber: duration,
          })
        : t('ad-expiry-days-left', {
            statNumber: 1,
          })
    }.`;
  };

  const handleDateClick = (date: string) => {
    dispatchFormData(FORM_DATA_AD_EXPIRY_DATE, date);
  };

  return (
    <Stack space={['medium', 'small']}>
      <Text>{t('ad-expiry-date-heading')}</Text>
      <Inline space="small" alignY="center" collapseBelow="tablet">
        <Text>{t('ad-expiry-date-text')}</Text>
        <AdExpiryDateInput
          max={maxDateStr}
          end={endDateStr}
          now={nowDateStr}
          endDate={endDate}
          handleDateClick={handleDateClick}
        />
        <Text>
          {t('ad-expiry-date-live-text')}{' '}
          {t('ad-expiry-date-duration', {
            duration: renderAdDuration(),
            AdDuration: (children) => VocabHtmlTags.strong(children),
          })}
        </Text>
      </Inline>
    </Stack>
  );
};

const mapStateToProps = (state: AppState) => ({
  expiryDate: state.formData.expiryDate,
  listingDate: state.formData.listingDate,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      dispatchFormData: formData,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(AdExpiryDate);
